import { useState } from 'react';
import { Search } from 'lucide-react';
import { crm, type MailRow, type ProjectRow } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useDebouncedSearch } from '@/lib/useDebouncedSearch';

interface Props {
  message: MailRow;
  onClose: () => void;
  onDone: () => Promise<void>;
}

/**
 * Eine Unterhaltung einem Projekt zuordnen.
 *
 * Steht hinter der Nachricht ein Kunde, ist die Liste auf dessen Projekte
 * vorgefiltert — das ist der Regelfall. Ein Projekt einer anderen Firma zu
 * wählen bleibt möglich; dafür gibt es unten den Hinweis, keine Sperre. Es
 * gibt Fälle, in denen die Zuordnung trotzdem stimmt, etwa wenn für einen
 * Kunden unter einer anderen Adresse geschrieben wird.
 */
export function AssignProjectDialog({ message, onClose, onDone }: Props) {
  const [suche, setSuche] = useState('');
  const [gewaehlt, setGewaehlt] = useState<ProjectRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    data,
    loading,
    error: sucheFehler,
  } = useDebouncedSearch<ProjectRow[]>(async () => {
    // Nur wenn hinter der Nachricht wirklich ein Kunde steht, lohnt sich die
    // Vorfilterung — bei einem bloßen Lead oder unbekanntem Absender gibt es
    // keinen Kunden, dessen Projekte man anzeigen könnte.
    const kunde =
      message.isCustomer && message.effectiveOrganizationId
        ? await crm.customers.byOrganization(message.effectiveOrganizationId)
        : null;
    return crm.projects.list({
      ...(kunde ? { customerId: kunde.id } : {}),
      search: suche.trim(),
      limit: 50,
    });
  }, [suche]);
  const projekte = data ?? [];

  const zuordnen = async () => {
    if (!gewaehlt || !message.threadId) return;
    setBusy(true);
    setError(null);
    try {
      await crm.projects.link(gewaehlt.id, { messageThreadIds: [message.threadId] });
      await onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Zuordnen fehlgeschlagen');
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-center overflow-y-auto bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Zu Projekt zuordnen"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="my-auto w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg">
        <h2 className="text-lg font-semibold">Zu Projekt zuordnen</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Die ganze Unterhaltung mit {message.fromAddress ?? 'diesem Absender'} wird
          angehängt.
        </p>

        <div className="relative mt-4">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Projekt suchen …"
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            aria-label="Projekte durchsuchen"
          />
        </div>

        {gewaehlt && gewaehlt.organizationId !== message.effectiveOrganizationId && (
          <p className="mt-3 text-sm text-warning-foreground">
            Dieses Projekt gehört zu {gewaehlt.customerName}, die Nachricht kommt von
            woanders. Zuordnen geht trotzdem.
          </p>
        )}

        <ul className="mt-3 max-h-64 overflow-auto rounded-md border border-border">
          {loading && (
            <li className="px-3 py-2 text-sm text-muted-foreground">Lade …</li>
          )}
          {!loading && sucheFehler && (
            <li className="px-3 py-2 text-sm text-destructive">{sucheFehler}</li>
          )}
          {!loading && !sucheFehler && projekte.length === 0 && (
            <li className="px-3 py-2 text-sm text-muted-foreground">
              Keine Projekte gefunden.
            </li>
          )}
          {!loading &&
            !sucheFehler &&
            projekte.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => setGewaehlt(p)}
                  className={cn(
                    'flex w-full flex-col items-start gap-0.5 border-b border-border px-3 py-2 text-left text-sm last:border-0 hover:bg-muted/40',
                    gewaehlt?.id === p.id && 'bg-muted/60',
                  )}
                >
                  <span className="font-medium">{p.title}</span>
                  <span className="text-xs text-muted-foreground">{p.customerName}</span>
                </button>
              </li>
            ))}
        </ul>

        {error && (
          <p className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button type="button" disabled={busy || !gewaehlt} onClick={() => void zuordnen()}>
            {busy ? 'Ordne zu …' : 'Zuordnen'}
          </Button>
        </div>
      </div>
    </div>
  );
}
